import { ReactNode, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ChevronLeft,
  LogOut,
  User,
  Menu,
  Users,
  LayoutDashboard,
  Rss,
  ClipboardList,
  Package,
  Sun,
  Moon,
  UserCircle,
  ChevronRight,
  RefreshCw,
  Stethoscope,
  Mail,
  Home,
  AlertTriangle,
  Bell,
  Crown,
  Building2,
  Settings as SettingsIcon,
  ShieldCheck,
  Activity,
  Handshake,
  Download
} from 'lucide-react';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { AppDownloadDialog } from '@/components/mobile/AppDownloadDialog';
import { AppDownloadBanner } from '@/components/mobile/AppDownloadBanner';
import { useAppDownloadPrompt } from '@/hooks/useAppDownloadPrompt';
import { SupportMenu } from '@/components/support/SupportMenu';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useAuth } from '@/contexts/AuthContext';
import { useFocusedPatient } from '@/hooks/useFocusedPatient';
import { usePatientRole } from '@/hooks/usePatientRole';
import { useCompany } from '@/hooks/useCompany';
import { useHasRole } from '@/hooks/useHasRole';
import { useTheme } from 'next-themes';
import { useIsMobile } from '@/hooks/use-mobile';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { AppSidebar } from './AppSidebar';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { SignedAvatar } from '@/components/ui/signed-avatar';
import { Separator } from '@/components/ui/separator';
import { supabase } from '@/integrations/supabase/client';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ThemedLogo } from '@/components/ui/themed-logo';
import { BottomTabBar } from './BottomTabBar';
import { LegalReacceptanceModal } from '@/components/legal/LegalReacceptanceModal';
import { AiAssistant } from '@/components/ai/AiAssistant';

export interface AppLayoutProps {
  children: ReactNode;
  showFAB?: boolean;
  title?: string;
  showBack?: boolean;
  onBack?: () => void;
  showNotifications?: boolean;
  showLogo?: boolean;
}

export function AppLayout({
  children,
  showFAB = false,
  title,
  showBack = false,
  onBack,
  showNotifications = false,
  showLogo = false
}: AppLayoutProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut, user, subscription, isLoggingOut } = useAuth();
  const { focusedPatient } = useFocusedPatient();
  const { canExecuteTasks, canViewDashboard, isCaregiver, isLoading: isRoleLoading } = usePatientRole(focusedPatient?.id);
  const { isCompanyManager, isCompanyManagerOrCoordinator, isCompanyMember, isLoading: isCompanyLoading } = useCompany();
  const isDpo = useHasRole('security_officer');
  const isCareosAdmin = useHasRole('careos_admin');
  const { theme, setTheme, resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const isMobile = useIsMobile();
  const appDownload = useAppDownloadPrompt();

  const isHomePage = location.pathname === '/app';
  const shouldShowBack = showBack || (!isHomePage && !showLogo);

  useEffect(() => {
    setMounted(true);
  }, []);

  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      return data;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    // Onboarding: name requirement
    // Only redirect if:
    // 1. Profile is loaded
    // 2. User is logged in
    // 3. full_name is missing
    // 4. Not already on the profile page
    if (mounted && user && profile && !profile.full_name && location.pathname !== '/app/profile') {
      console.log('Redirecting to profile for naming...');
      navigate('/app/profile');
    }
  }, [mounted, user, profile, location.pathname, navigate]);

  const { data: inventoryAlerts = [] } = useQuery({
    queryKey: ['inventory-alerts', focusedPatient?.id],
    queryFn: async () => {
      if (!focusedPatient?.id) return [];
      const { data, error } = await supabase
        .from('inventory')
        .select('id')
        .eq('patient_id', focusedPatient.id)
        .neq('status', 'ok');
      if (error) throw error;
      return data || [];
    },
    enabled: !!focusedPatient?.id
  });

  const handleLogout = async () => {
    if (isLoggingOut) return;
    await signOut();
  };

  const toggleTheme = () => {
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
  };

  const currentTheme = mounted ? resolvedTheme : 'light';

  // Desktop layout with sidebar
  if (!isMobile) {
    return (
      <SidebarProvider>
        <div className="min-h-screen flex w-full">
          <AppSidebar />
          <div className="flex-1 flex flex-col">
            <header className="sticky top-0 z-40 bg-background border-b border-border pt-[env(safe-area-inset-top)]">
              <div className="px-4 py-3 flex items-center gap-3">
                <SidebarTrigger />
                {shouldShowBack && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="shrink-0"
                    onClick={onBack || (() => window.history.back())}
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                )}
                {title ? (
                  <h1 className="font-semibold text-lg truncate flex-1">{title}</h1>
                ) : (
                  <div className="flex-1" />
                )}
                <SupportMenu variant="icon" />
                <NotificationBell />
              </div>
            </header>

            <LegalReacceptanceModal />
            <AiAssistant />
            <main className="flex-1 p-4 max-w-5xl">
              {children}
            </main>

          </div>
        </div>
      </SidebarProvider>
    );
  }

  // Mobile layout
  return (
    <div className="min-h-screen bg-background pb-20">
      <header className="sticky top-0 z-40 bg-background border-b border-border pt-[env(safe-area-inset-top)]">
        <div className={`py-3 flex items-center gap-2 ${shouldShowBack ? "px-0" : "pl-4"} pr-2`}>
          {shouldShowBack && (
            <Button
              variant="ghost"
              size="icon"
              className="shrink-0"
              onClick={onBack || (() => window.history.back())}
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
          )}
          {!shouldShowBack && !title && (
            <ThemedLogo className="h-8 w-8 object-contain" />
          )}
          {title ? (
            <h1 className="font-semibold text-lg truncate flex-1">{title}</h1>
          ) : (
            <div className="flex-1" />
          )}
          <NotificationBell />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="shrink-0">
                <Menu className="w-5 h-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-64 max-h-[80vh] overflow-y-auto"
              collisionPadding={50}
            >
              {/* User Info with Avatar */}
              {user && (
                <div className="px-2 py-2 mx-2 mb-1 flex items-center gap-3">
                  <SignedAvatar
                    src={profile?.avatar_url}
                    bucket="avatars"
                    className="h-10 w-10"
                    fallback={
                      <span className="bg-primary/10 text-primary flex items-center justify-center w-full h-full">
                        {profile?.full_name?.charAt(0) || user.email?.charAt(0)?.toUpperCase() || '?'}
                      </span>
                    }
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {profile?.full_name || user.email}
                    </p>
                    {profile?.full_name && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                        <Mail className="h-3 w-3 shrink-0" />
                        {user.email}
                      </p>
                    )}
                  </div>
                </div>
              )}

              <DropdownMenuSeparator />

              {/* Home always first */}
              <DropdownMenuItem onClick={() => navigate('/app')}>
                <Home className="w-4 h-4 mr-2" />
                {t('nav.home')}
              </DropdownMenuItem>

              {/* Company Dashboard - For managers and coordinators */}
              {isCompanyManagerOrCoordinator && (
                <DropdownMenuItem onClick={() => navigate('/app/company')}>
                  <Building2 className="w-4 h-4 mr-2" />
                  {t('company.title')}
                </DropdownMenuItem>
              )}

              {/* Company Settings - Only for managers */}
              {isCompanyManager && (
                <DropdownMenuItem onClick={() => navigate('/app/company/settings')} className="pl-8">
                  <SettingsIcon className="w-4 h-4 mr-2" />
                  <span className="text-sm">{t('company.settingsTitle')}</span>
                </DropdownMenuItem>
              )}

              {/* DPO Panel - Only for users with security_officer role */}
              {isDpo === true && (
                <DropdownMenuItem onClick={() => navigate('/app/dpo')}>
                  <ShieldCheck className="w-4 h-4 mr-2" />
                  Painel do Encarregado
                </DropdownMenuItem>
              )}

              {/* CareOS Admin — internal staff only */}
              {isCareosAdmin === true && (
                <DropdownMenuItem onClick={() => navigate('/app/admin')}>
                  <Activity className="w-4 h-4 mr-2" />
                  Painel administrativo
                </DropdownMenuItem>
              )}

              <DropdownMenuSeparator />

              {/* Focused Patient Section */}
              <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                {t('nav.focusedPatient')}
              </DropdownMenuLabel>

              {focusedPatient ? (
                <>

                  <div className="px-2 py-2 flex items-center gap-3 bg-primary/5 rounded-md mx-2 mb-1">
                    <SignedAvatar
                      src={focusedPatient.photo_url}
                      bucket="patient-photos"
                      className="h-8 w-8"
                      fallback={
                        <span className="bg-primary/10 text-primary text-sm flex items-center justify-center w-full h-full">
                          {focusedPatient.nickname.charAt(0).toUpperCase()}
                        </span>
                      }
                    />
                    <span className="font-medium text-sm truncate">
                      {focusedPatient.nickname}
                    </span>
                  </div>

                  <DropdownMenuItem
                    onClick={() => navigate('/app/patients')}
                    className="text-xs text-muted-foreground"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    {t('nav.changePatient')}
                  </DropdownMenuItem>

                  <DropdownMenuSeparator className="my-1" />

                  <DropdownMenuItem onClick={() => navigate(`/app/patients/${focusedPatient.id}`)}>
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    {t('nav.panel')}
                  </DropdownMenuItem>
                  {canViewDashboard && (
                    <DropdownMenuItem onClick={() => navigate(`/app/family/${focusedPatient.id}`)}>
                      <Rss className="w-4 h-4 mr-2" />
                      {t('nav.followPatient')}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => navigate(`/app/patients/${focusedPatient.id}/care-plan`)}>
                    <ClipboardList className="w-4 h-4 mr-2" />
                    {t('carePlan.title')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate(`/app/inventory/${focusedPatient.id}`)} className="relative">
                    <div className="relative mr-2">
                      <Package className="w-4 h-4" />
                      {inventoryAlerts.length > 0 && (
                        <AlertTriangle className="w-2.5 h-2.5 text-warning absolute -top-0.5 -right-0.5" />
                      )}
                    </div>
                    <span className="flex-1">{t('nav.inventory')}</span>
                    {inventoryAlerts.length > 0 && (
                      <Badge variant="destructive" className="h-5 min-w-5 px-1 flex items-center justify-center text-[12px] ml-2">
                        {inventoryAlerts.length}
                      </Badge>
                    )}
                  </DropdownMenuItem>

                  {!isRoleLoading && isCaregiver && (
                    <DropdownMenuItem onClick={() => navigate(`/app/caregiver/${focusedPatient.id}`)}>
                      <Stethoscope className="w-4 h-4 mr-2" />
                      {t('nav.caregiverMode')}
                    </DropdownMenuItem>
                  )}
                </>
              ) : (
                <DropdownMenuItem onClick={() => navigate('/app/patients')}>
                  <UserCircle className="w-4 h-4 mr-2" />
                  {t('nav.choosePatient')}
                  <ChevronRight className="w-4 h-4 ml-auto" />
                </DropdownMenuItem>
              )}

              <DropdownMenuSeparator />

              {/* Account Section */}
              <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                {t('nav.account')}
              </DropdownMenuLabel>

              <DropdownMenuItem onClick={() => navigate('/app/profile')}>
                <User className="w-4 h-4 mr-2" />
                {t('nav.myProfile')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/app/patients')}>
                <Users className="w-4 h-4 mr-2" />
                {t('nav.myPatients')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/app/settings/notifications')}>
                <Bell className="w-4 h-4 mr-2" />
                {t('settings.notifications')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/app/subscription')} className="flex items-center justify-between">
                <span className="flex items-center">
                  <Crown className={`w-4 h-4 mr-2 ${subscription.subscribed ? 'text-primary' : ''}`} />
                  {t('nav.subscription')}
                </span>
                {subscription.subscribed && (
                  <Badge variant="default" className="ml-2 text-[12px]">{t('subscription.activeShort')}</Badge>
                )}
              </DropdownMenuItem>

              {/* Create Company - Only for non-members */}
              {!isCompanyLoading && !isCompanyMember && (
                <DropdownMenuItem onClick={() => navigate('/app/company/new')}>
                  <Building2 className="w-4 h-4 mr-2" />
                  {t('company.createCompany')}
                </DropdownMenuItem>
              )}

              <DropdownMenuItem onClick={() => navigate('/parceiros/painel')}>
                <Handshake className="w-4 h-4 mr-2" />
                Espaço do parceiro
              </DropdownMenuItem>

              {appDownload.eligible && (
                <DropdownMenuItem onClick={() => appDownload.openStore('menu')}>
                  <Download className="w-4 h-4 mr-2" />
                  {t('appDownload.menuItem')}
                </DropdownMenuItem>
              )}

              <SupportMenu variant="menu-items" />

              <DropdownMenuItem asChild>
                <LanguageSwitcher variant="dropdown" />
              </DropdownMenuItem>

              <DropdownMenuItem onClick={toggleTheme}>
                {currentTheme === 'dark' ? (
                  <Sun className="w-4 h-4 mr-2" />
                ) : (
                  <Moon className="w-4 h-4 mr-2" />
                )}
                {currentTheme === 'dark' ? t('settings.lightTheme') : t('settings.darkTheme')}
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              <DropdownMenuItem
                onClick={handleLogout}
                disabled={isLoggingOut}
                className="text-destructive focus:text-destructive"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {isLoggingOut ? t('common.loading') : t('auth.logoutAccount')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <LegalReacceptanceModal />
      <AiAssistant />
      <main className="px-0">
        {children}
      </main>

      <AppDownloadDialog
        open={appDownload.dialogOpen}
        onDownload={appDownload.openStore}
        onDismiss={appDownload.dismissDialog}
        onShown={appDownload.trackShown}
      />
      {appDownload.showBanner && !appDownload.dialogOpen && (
        <AppDownloadBanner
          onDownload={appDownload.openStore}
          onClose={appDownload.closeBanner}
          onShown={appDownload.trackShown}
        />
      )}

      <BottomTabBar />
    </div>
  );
}